import React from "react";
import { motion } from "motion/react";
import { Section } from "../components/section";
import { SEO } from "../components/seo";
import { 
  ShoppingBag, 
  LayoutDashboard, 
  CalendarCheck, 
  Globe2, 
  Zap, 
  ShieldCheck,
  Code2,
  Workflow
} from "lucide-react";
import { ContactForm } from "../components/contact-form";
import { Process } from "../components/process";
import { CostEstimator } from "../components/cost-estimator";

const servicesList = [
  {
    title: "Admin Dashboard Systems",
    icon: <LayoutDashboard size={40} className="text-violet-400" />,
    description: "Custom internal tools and staff management platforms with roles, authentication, and CRUD functionality. I build systems that centralize your data and automate complex business logic, allowing you to manage your operations with precision and ease.",
    features: ["Role-based access control", "Real-time data visualization", "Inventory & resource tracking", "Automated reporting systems"],
    color: "from-violet-500/10 to-transparent"
  },
  {
    title: "Booking & Reservation Flow",
    icon: <CalendarCheck size={40} className="text-fuchsia-400" />,
    description: "Highly interactive reservation systems designed for service businesses. From automated email/SMS reminders to seamless calendar syncing and secure deposit handling, I create frictionless booking experiences for your clients.",
    features: ["Live availability management", "Secure payment integration", "Google/Outlook calendar sync", "Automated client notifications"],
    color: "from-fuchsia-500/10 to-transparent"
  },
  {
    title: "E-commerce & Listings",
    icon: <ShoppingBag size={40} className="text-cyan-400" />,
    description: "Advanced product listings or automotive portals with complex filtering and multi-language support. I focus on high-performance catalogs that are fully optimized for SEO, ensuring your products reach the right audience.",
    features: ["Advanced faceted search", "Dynamic SEO metadata", "Multilingual (i18n) support", "Optimized image delivery"],
    color: "from-cyan-500/10 to-transparent"
  },
  {
    title: "Workflow Automation",
    icon: <Workflow size={40} className="text-emerald-400" />,
    description: "Automating repetitive tasks via custom APIs, bot integrations, and platform synchronization. I help businesses save hundreds of hours by connecting disparate systems and automating manual data entry and communication flows.",
    features: ["Custom API connectors", "Slack/Discord bot integration", "Cross-platform data syncing", "Automated task scheduling"],
    color: "from-emerald-500/10 to-transparent"
  },
  {
    title: "Full-stack Web Support",
    icon: <Globe2 size={40} className="text-pink-400" />,
    description: "End-to-end management of web applications. I handle everything from initial hosting setup and domain/DNS configuration to ongoing security audits and performance monitoring, so you can focus on your business.",
    features: ["SSL & security hardening", "DNS & domain management", "Managed server maintenance", "24/7 uptime monitoring"],
    color: "from-pink-500/10 to-transparent"
  },
  {
    title: "Legacy Code Migration",
    icon: <Zap size={40} className="text-orange-400" />,
    description: "Modernizing older PHP/MySQL applications by migrating them to modern React/Next.js and TypeScript ecosystems. I ensure complete data integrity while delivering a massive boost in performance and a fresh, modern UI.",
    features: ["Modern framework upgrades", "Secure data migration", "Performance & speed boost", "Responsive UI redesign"],
    color: "from-orange-500/10 to-transparent"
  },
];

export function Services() {
  return (
    <>
      <SEO 
        title="Services"
        description="Professional web development services including admin dashboards, booking systems, e-commerce platforms, workflow automation, and full-stack support. Custom business solutions using PHP, MySQL, Next.js, and React."
        keywords="web development services, custom admin dashboard, booking system, e-commerce development, workflow automation, full stack developer, PHP developer, React developer, business solutions"
        canonical="/services"
      />
      <div className="pt-24 pb-12">
        <div className="fixed inset-0 z-0 pointer-events-none opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(#1a1a1a_1px,transparent_1px)] [background-size:40px_40px]" />
        </div>

        <Section 
          id="services-header" 
          title="Solutions Catalog" 
          subtitle="I build robust, business-oriented platforms that solve real-world operational challenges."
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className="relative bg-neutral-900/40 backdrop-blur-md border border-neutral-800/50 p-10 rounded-[2.5rem] hover:border-neutral-600 transition-all duration-500 group overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
                
                <div className="relative z-10">
                  <div className="mb-8 p-5 bg-neutral-950 border border-neutral-800 rounded-2xl w-fit group-hover:scale-110 transition-transform duration-500">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-black mb-4 uppercase tracking-tight text-white group-hover:text-white transition-colors">{service.title}</h3>
                  <p className="text-neutral-500 text-sm md:text-base mb-10 leading-relaxed font-medium min-h-[100px]">
                    {service.description}
                  </p>
                  <ul className="space-y-4 pt-8 border-t border-neutral-800/50">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-neutral-500 group-hover:text-neutral-300 transition-colors">
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-600 shadow-[0_0_10px_rgba(139,92,246,0.5)]" /> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        <Section 
          id="estimator" 
          title="Project Estimator" 
          subtitle="Get an instant estimate for your next digital solution."
          dark
        >
          <CostEstimator />
        </Section>

        <Section 
          id="process" 
          title="How I Work" 
          subtitle="A structured approach to delivering high-performance digital solutions."
        >
          <Process />
        </Section>

        <Section 
          id="service-cta" 
          title="Ready to Build?" 
          subtitle="Let's discuss how we can automate your business or build your next platform."
          dark
        >
          <ContactForm />
        </Section>
      </div>
    </>
  );
}